import circleBg from '@/assets/notifications/resolution-won-some/medallion-circle-bg.svg';
import trophy from '@/assets/notifications/resolution-won-some/medallion-trophy.png';
import tickIcon from '@/assets/notifications/resolution-won-some/icon-tick-circle.svg';
import minusIcon from '@/assets/notifications/resolution-won-some/icon-minus-circle.svg';
import { formatCurrency } from '@/lib/format-currency';
import { POPUP_CTA } from '@/lib/notifications/popup-cta';
import { marketDeepLinkId, marketTitle, metaNumber, metaString } from '@/lib/notifications/meta';
import { cn } from '@/lib/utils';
import { Medallion } from '../Medallion';
import { PopupCta, PopupLinkCta } from '../PopupCta';
import type { PopupViewProps } from '../types';

const CTA = POPUP_CTA['resolution-won-some'];

/** One settled leg of a mixed-outcome resolution (Figma fact-row). */
interface Leg {
  name: string;
  won: boolean;
  /** Payout in USD dollars; 0 for a lost/void leg. */
  amount?: number;
}

/**
 * Normalize the frozen per-event `meta.legs` into displayable rows. The API
 * contract shape is `{ marketName, amount, won }`; legacy `{ name|label, outcome
 * }` producers are still tolerated as a fallback.
 */
function parseLegs(meta: PopupViewProps['notification']['meta']): Leg[] {
  const raw = meta?.legs;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry) => {
    if (entry === null || typeof entry !== 'object') return [];
    const leg = entry as Record<string, unknown>;
    const name = [leg.marketName, leg.name, leg.label, leg.leg].find((v) => typeof v === 'string' && v.length > 0) as
      | string
      | undefined;
    if (!name) return [];
    const outcome = typeof leg.outcome === 'string' ? leg.outcome.toUpperCase() : undefined;
    const won = typeof leg.won === 'boolean' ? leg.won : outcome ? outcome === 'WON' : false;
    const amount = typeof leg.amount === 'number' && Number.isFinite(leg.amount) ? leg.amount : undefined;
    return [{ name, won, amount }];
  });
}

/** Settlement / leg amounts arrive in USD dollars → cents-based formatter. */
function money(dollars: number): string {
  return formatCurrency(Math.round(dollars * 100));
}

/**
 * MARKET_RESOLUTION · WON_SOME — mixed-outcome win. Trophy medallion (no
 * confetti), a big green net payout, and a per-leg breakdown: won legs get a
 * green tick + green amount, lost/void legs a neutral minus + "-". Reads the
 * frozen per-event meta shape (`legs`/`correctCount`/`totalCount`/
 * `settlementAmount`/`eventName`). Matches `.arc111/figma/resolution-won-some/`.
 */
export function ResolutionWonSomePopup({ notification, onAction }: PopupViewProps) {
  const meta = notification.meta;
  const eventName = metaString(meta, 'eventName') ?? marketTitle(meta);
  const legs = parseLegs(meta);
  const settlementAmount =
    metaNumber(meta, 'settlementAmount') ?? metaNumber(meta, 'netAmount') ?? metaNumber(meta, 'amount');
  const correctCount = metaNumber(meta, 'correctCount') ?? metaNumber(meta, 'wonCount') ?? legs.filter((l) => l.won).length;
  const totalCount = metaNumber(meta, 'totalCount') ?? (legs.length || undefined);
  const heading =
    correctCount !== undefined && totalCount !== undefined
      ? `You called ${correctCount} of ${totalCount}`
      : (metaString(meta, 'title') ?? 'You called some');
  const secondary = CTA.secondary;
  const hasMarket = marketDeepLinkId(meta) !== undefined;

  return (
    <>
      <Medallion circleSrc={circleBg} illustrationSrc={trophy} tone="neutral" alt="" />

      <div className="flex flex-col items-center gap-3 self-stretch px-8">
        <h2 className="text-3xl font-semibold tracking-[-0.6px] text-ink">{heading}</h2>

        {eventName && (
          <p className="text-xl text-ink">
            <span className="font-bold">{`“${eventName}”`}</span> resolved
          </p>
        )}

        {settlementAmount !== undefined && (
          <>
            <p className="text-[46px] font-bold leading-none tracking-[-0.92px] text-success">{`+${money(settlementAmount)}`}</p>
            <p className="text-xl text-success">Added to your balance</p>
          </>
        )}

        {legs.length > 0 && (
          <>
            <div className="my-1 h-px w-full bg-border" />
            <ul className="flex w-full flex-col gap-3" data-testid="resolution-legs">
              {legs.map((leg, i) => (
                <li key={`${leg.name}-${i}`} className="flex items-center justify-between gap-3">
                  <span className="flex min-w-0 items-center gap-2">
                    <img
                      src={leg.won ? tickIcon : minusIcon}
                      alt=""
                      aria-hidden
                      // The lost-leg minus glyph is a dark-grey static SVG that
                      // vanishes on the dark card — lift it in dark mode.
                      className={cn('size-6 shrink-0', !leg.won && 'dark:invert')}
                    />
                    <span className={cn('truncate text-xl', leg.won ? 'text-success' : 'text-ink')}>{leg.name}</span>
                  </span>
                  <span className={cn('shrink-0 text-xl', leg.won ? 'text-success' : 'text-ink')}>
                    {leg.won && leg.amount !== undefined ? `+${money(leg.amount)}` : '-'}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      <div className="flex flex-col items-center gap-3">
        <PopupCta
          label={CTA.primary.label}
          width={320}
          onClick={() => onAction(CTA.primary.action)}
          testId="popup-cta-primary"
        />
        {secondary && hasMarket && (
          <PopupLinkCta label={secondary.label} onClick={() => onAction(secondary.action)} testId="popup-cta-secondary" />
        )}
      </div>
    </>
  );
}
